import { useMemo } from "react";
import { useProductData2 } from "@context/products";
import { useProductPricing } from "@hooks/usePrices";

/**
 * Custom hook to fetch and price the products in a bundle.
 *
 * @param {Array} bundleProducts - Array of bundle product objects, each one containing product handle and quantity
 * @param {string} countryCode - The country code for pricing.
 * @param {boolean} enabled - Whether the hook should be enabled.
 * @returns {Object} - The result of `useProductPricing` with the bundle products and their quantities.
 */
export default function useBundleProductsWithPrices(bundleProducts, countryCode, enabled = true) {
  const hasBundleProducts = bundleProducts?.length > 0;
  const bundleHandles = useMemo(() => bundleProducts?.map((b) => b.handle) ?? [], [bundleProducts]);

  const { data: productsBundle } = useProductData2(
    bundleHandles,
    "long",
    countryCode,
    enabled && hasBundleProducts
  );

  const productsBundleWithQuantity = useMemo(
    () =>
      hasBundleProducts
        ? productsBundle.map((p) => ({
            ...p,
            quantity: bundleProducts?.find((b) => b.handle === p?.node?.handle)?.quantity ?? 1,
          }))
        : [],
    [productsBundle, bundleProducts, hasBundleProducts]
  );

  return useProductPricing({ products: productsBundleWithQuantity });
}
